import { Box, Grid, Text, Button, Select, TextInput } from 'grommet';
import React, { useEffect, useRef } from 'react';
import { safeLoanCalculation, generateTrackerEmail } from '../utils'
import { getPricing } from '../api';
import { PrimaryButton, GapBox, FrostedGlassBox } from '../components'
import { isSafeNumInput } from "../utils";
import './InfoSection.css'
import { useAuth } from "../context";

// PricingSection.js - Modified with universal pricing fields and panel-specific max_lvr

const PricingSection = ({
  pricingData,
  setPricingData,
  panels,
  updatePanel,
  sharedPanelProps,
  updateSharedPanelProps,
  isMobile,
  loanSummary
}) => {
  const { tokens } = useAuth();
  // Helper function to update a specific panel's values
  const updatePanelValues = (id, newValues) => {
    updatePanel(id, newValues);
  };
  const alertRef = useRef(false);
  
  // Effect to update max_lvr in each panel when pricing data is received
  useEffect(() => {
    // Skip if no pricing data or empty pricing data
    if (!pricingData || Object.keys(pricingData).length === 0) {
      return;
    }

    console.log("PricingSection: Received pricing data:", pricingData);

    // Check if pricing data contains panel-specific max_lvr information
    if (pricingData.max_lvr && typeof pricingData.max_lvr === 'object') {
      // Update each panel with its respective max_lvr
      console.log(pricingData.max_lvr)
      Object.entries(pricingData.max_lvr).forEach(([panelId, panelPricingData]) => {
        if (panelPricingData) {
          const numericPanelId = parseInt(panelId, 10);

          // Find the panel with this ID
          const panel = panels.find(p => p.id === numericPanelId);

          if (panel) {
            updatePanelValues(numericPanelId, {
                max_lvr: panelPricingData
              });
            // if (panel.values.max_lvr !== panelPricingData) {

            //   // Update only the max_lvr for this panel
            //   updatePanelValues(numericPanelId, {
            //     max_lvr: panelPricingData
            //   });
            // }
          }
        }
      });
    } else if ('max_lvr' in pricingData) {
      // If there's a global max_lvr, apply to all panels
      panels.forEach(panel => {
        if (panel.id.max_lvr !== pricingData) {
          // console.log(panel.id.max_lvr, pricingData)
          updatePanelValues(panel.id, {
            max_lvr: pricingData
          });
        }
      });
    }
  }, [pricingData, panels]);


  return (
    <FrostedGlassBox margin={{ top: '0.5rem' }}>
      <div className="container">
        <Box margin={'1.5rem 0 0 1.5rem'}>
          <Text flex='shrink' size="large">
            {`Pricing`}
          </Text>
          <GapBox />

          {/* Input fields for loan term and borrower */}
          <Grid columns={{ count: isMobile ? 1 : 2, size: 'auto' }} gap={'small'}>
            <Grid columns={{ count: 2, size: 'auto' }} gap={'small'}>
              <Box className={'field-box'}>
                <Text flex='shrink' margin={{ vertical: 'auto' }} size="xsmall">
                  Loan Term (months)<Text color={'red'} size="xsmall">*</Text>
                </Text>
                <TextInput
                  flex='shrink'
                  margin={{ vertical: 'auto' }}
                  width='100%'
                  className={'field-input'}
                  onChange={(e) => {
                    if (isSafeNumInput(e.target.value))
                      updateSharedPanelProps({ loan_term: e.target.value });
                  }}
                  onBlur={(e) => {
                    const validatedValue = Math.min(Math.max(parseFloat(sharedPanelProps.loan_term) || 0, 1), 24);
                    updateSharedPanelProps({ loan_term: validatedValue });
                  }}
                  value={`${sharedPanelProps?.loan_term || ''}`}
                />
              </Box>
              <Box className={'field-box'} width={{ max: isMobile ? '94.5%' : '100%' }}>
                  <Text flex='shrink' margin={{ vertical: 'auto' }} size="xsmall">
                      Turnaround Preference<Text color={'red'} size="xsmall">*</Text>
                  </Text>
                  <Select
                      margin={{ vertical: 'auto', right: isMobile ? '24px' : '3.55rem' }}
                      className={'field-select'}
                      options={['Standard Processing', 'Priority Processing']}
                      onChange={(e) => updateSharedPanelProps({priority: e.value})}
                      value={`${sharedPanelProps?.priority || 'Standard Processing'}`} />
              </Box>
            </Grid>
            <Grid columns={{ count: 2, size: 'auto' }} gap={'small'}>
              <Box className={'field-box'}>
                <Text flex='shrink' margin={{ vertical: 'auto' }} size="xsmall">
                  Guarantor Count<Text color={'red'} size="xsmall">*</Text>
                </Text>
                <TextInput
                  flex='shrink'
                  margin={{ vertical: 'auto' }}
                  className={'field-input'}
                  onChange={(e) => {
                    if (isSafeNumInput(e.target.value))
                      updateSharedPanelProps({ guarantor_count: e.target.value?.replaceAll(' ', '') });
                  }}
                  value={`${sharedPanelProps?.guarantor_count || ''}`}
                />
              </Box>
              <Box className={'field-box'} width={{ max: isMobile ? '94.5%' : '100%' }}>
                  <Text flex='shrink' margin={{ vertical: 'auto' }} size="xsmall">
                      Borrower Structure<Text color={'red'} size="xsmall">*</Text>
                  </Text>
                  <Select
                      margin={{ vertical: 'auto', right: isMobile ? '24px' : '3.55rem' }}
                      className={'field-select'}
                      options={['No Trust Involved', 'One Trust Involved', 'Multiple Trust Involved']}
                      onChange={(e) => updateSharedPanelProps({borrower_structure: e.value})}
                      value={`${sharedPanelProps?.borrower_structure || 'No Trust Involved'}`} />
              </Box>
              {/* <Box></Box>
              <Box padding={{ right: '0.5rem', top: '0.5rem' }} margin={!isMobile && { top: '0.8rem' }}>

              </Box> */}
            </Grid>
          </Grid>

          {/* Loan Summary Section */}
          <Grid columns={{ count: isMobile ? 1 : 2, size: 'auto' }} gap={'small'}>
            <Grid columns={{ count: 2, size: 'auto' }} gap={'small'}>
              <Box>
                <Text flex='shrink' size="small">
                  Total EMV
                </Text>
                <Text size={!isNaN(loanSummary?.totalEMV) ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                  {loanSummary?.totalEMV ?
                    !isNaN(loanSummary?.totalEMV) ?
                      `$${new Intl.NumberFormat("en-AU").format(loanSummary?.totalEMV)}`
                      : loanSummary?.totalEMV
                    : '--'}
                </Text>
              </Box>
              <Box>
                <Text flex='shrink' size="small">
                  Total Loan Amount
                </Text>
                <Text size={!isNaN(loanSummary?.totalLoanAmount) ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                  {loanSummary?.totalLoanAmount ?
                    !isNaN(loanSummary?.totalLoanAmount) ?
                      `$${new Intl.NumberFormat("en-AU").format(loanSummary?.totalLoanAmount)}`
                      : loanSummary?.totalLoanAmount
                    : '--'}
                </Text>
              </Box>
            </Grid>
            <Grid columns={{ count: 2, size: 'auto' }} gap={'small'}>
              <Box>
                <Text flex='shrink' size="small">
                  Total LVR
                </Text>
                <Text size={!isNaN(loanSummary?.totalLVR) ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                  {loanSummary?.totalLVR ?
                    !isNaN(loanSummary?.totalLVR) ?
                      `${(loanSummary?.totalLVR * 100)?.toFixed(2)}%`
                      : loanSummary?.totalLVR
                    : '--'}
                </Text>
              </Box>
            <Box className={'field-box'} width={{ max: isMobile ? '94.5%' : '100%' }}>
                <Text flex='shrink' margin={{ vertical: 'auto' }} size="xsmall">
                    DA Status<Text color={'red'} size="xsmall">*</Text>
                </Text>
                <Select
                    margin={{ vertical: 'auto', right: isMobile ? '24px' : '3.55rem' }}
                    className={'field-select'}
                    options={['--', 'N/A', 'Approved', 'Pending']}
                    onChange={(e) => updateSharedPanelProps({da_status: e.value})}
                    value={`${sharedPanelProps?.da_status || '--'}`} />
            </Box>
            </Grid>
          </Grid>
          <Box width={'15rem'} alignSelf="center">
            <Button
              alignSelf="center"
              margin={{ left: '-1.5rem' }}
              style={{ width: isMobile ? '80%' : '70%', height: '3rem', fontSize: 'medium'}}
              label='Get Pricing'
              size='small'
              disabled={!safeLoanCalculation(panels, loanSummary, sharedPanelProps)} // Use updated function
              onClick={() => {
                // Convert panels to format expected by API
                const pricingInput = panels.reduce((acc, panel) => {
                  if (panel.values) {
                    acc[panel.id.toString()] = Object.keys(panel.values)
                      .filter(key => key !== 'storage')
                      .reduce((obj, key) => {
                        obj[key] = panel.values[key];
                        return obj;
                      }, {});
                    ;
                  }
                  return acc;
                }, {});

                // Call API with panels data
                getPricing(tokens?.idToken, pricingInput, sharedPanelProps).then((res) => {
                  console.log("Pricing API response:", res);
                  setPricingData(res);
                  // The useEffect above will handle updating max_lvr in panels
                });
              }}
            />
          </Box>
          {/* Universal Pricing results display */}
          <Box margin={{ top: '1.5rem' }}> </Box>
          <Grid columns={{ count: isMobile ? 2 : 4, size: '25%' }} gap='small' style={{ justifyContent: 'normal', minHeight: '3rem' }} >
            <Box>
              <Text flex='shrink' size="small">
                Interest Rate (per annum)
              </Text>
              <Text size={!isNaN(pricingData?.interest_rate) ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                {pricingData?.interest_rate ?
                  !isNaN(pricingData?.interest_rate) ?
                    `${(pricingData?.interest_rate * 100)?.toFixed(2)}%`
                    : pricingData?.interest_rate
                  : '--'}
              </Text>
            </Box>
            <Box>
              <Text flex='shrink' size="small">
                Line Fee (per month)
              </Text>
              <Text size={!isNaN(pricingData?.line_fee) ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                {pricingData?.line_fee ?
                  !isNaN(pricingData?.line_fee) ?
                    `${(pricingData?.line_fee * 100)?.toFixed(2)}%`
                    : pricingData?.line_fee
                  : '--'}
              </Text>
            </Box>
            <Box>
              <Text flex='shrink' size="small">
                Establishment Fee
              </Text>
              <Text size={!isNaN(pricingData?.establishment_fee) ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                {pricingData?.establishment_fee ?
                  !isNaN(pricingData?.establishment_fee) ?
                    `${(pricingData?.establishment_fee * 100)?.toFixed(2)}%`
                    : pricingData?.establishment_fee
                  : '--'}
              </Text>
            </Box>
            <Box>
              <Text flex='shrink' size="small">
                Upfront Fee
              </Text>
              <Text size={!isNaN(pricingData?.upfront_fee) && pricingData?.upfront_fee !== "By Quote" ? 'large' : 'small'} margin={'0rem 1rem 0 0'}>
                {pricingData?.upfront_fee ?
                  !isNaN(pricingData?.upfront_fee) ?
                    `$${new Intl.NumberFormat("en-AU").format(pricingData?.upfront_fee)}`
                    : pricingData?.upfront_fee
                  : '--'}
              </Text>
            </Box>
          </Grid>

          {/* Email tracker */}
          <Box margin={{ top: '1.5rem' }}> </Box>
          {/* <Grid columns={{ count: isMobile ? 2 : 4, size: '25%' }} gap='small' style={{ justifyContent: 'normal', minHeight: '3rem' }} >
            <Box></Box>
            <Box></Box>
            <Box></Box>
            <Box margin={{ bottom: '0.5rem' }}>
              <PrimaryButton
                height={'3rem'}
                style={{ width: '9rem' }}
                onClick={(e) => {
                  // Prevent event bubbling
                  e.stopPropagation();

                  // Debounce the click to prevent double execution
                  if (!alertRef.current) {
                    alertRef.current = true;

                    // Convert panels to format expected by email tracker
                    const inputValuesFormat = panels.reduce((acc, panel) => {
                      if (panel.values?.gnaf) {
                        acc[panel.id.toString()] = panel.values;
                      }
                      return acc;
                    }, {});
                    window.alert("Tracker copied to clipboard. It's ready to paste into your email.");
                    generateTrackerEmail(inputValuesFormat, sharedPanelProps, loanSummary, pricingData);
                    

                    // Reset the flag after a short delay to ensure full completion
                    setTimeout(() => {
                      alertRef.current = false;
                    }, 300);
                  }
                }}
                label={'Email Tracker'}
              />
            </Box>
          </Grid> */}
        </Box>
      </div>
    </FrostedGlassBox>
  );
};

export { PricingSection }